import React, { useState } from 'react';
import { 
  ChevronLeft,
  ChevronRight,
  Clock,
  Calendar as CalendarIcon,
  Plus,
  PlayCircle,
  Video
} from 'lucide-react';

const Schedule = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 
                 'August', 'September', 'October', 'November', 'December'];

  // Sample schedule data
  const scheduleEvents = [
    {
      id: 1,
      title: "Advanced Sales Techniques",
      type: "Course",
      time: "10:00 AM",
      duration: "1h 30m",
      instructor: "Sarah Johnson",
      thumbnail: "/api/placeholder/64/64"
    },
    {
      id: 2,
      title: "Negotiation Skills Workshop",
      type: "Live Session",
      time: "2:00 PM",
      duration: "1h",
      instructor: "Michael Chen",
      thumbnail: "/api/placeholder/64/64"
    }
  ];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month + 1, 0).getDate();
  };

  const getMonthDays = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay();
    const days = [];

    // Add empty cells for days before the first of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }

    return days;
  };

  const EventCard = ({ event }) => (
    <div className="flex items-start gap-4 p-4 bg-white rounded-lg border border-gray-100 hover:border-navy-200 transition-all">
      <div className="relative">
        <img
          src={event.thumbnail}
          alt={event.title}
          className="w-16 h-16 rounded-lg object-cover"
        />
        <div className={`absolute -top-2 -right-2 p-1.5 rounded-full ${
          event.type === 'Live Session' ? 'bg-red-500' : 'bg-navy-600'
        }`}>
          {event.type === 'Live Session' ? 
            <Video size={12} className="text-white" /> : 
            <PlayCircle size={12} className="text-white" />
          }
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className={`text-xs px-2 py-1 rounded-full ${
            event.type === 'Live Session'
              ? 'bg-red-50 text-red-600'
              : 'bg-navy-50 text-navy-600'
          }`}>
            {event.type}
          </span>
        </div>
        <h3 className="font-medium text-navy-900 truncate">{event.title}</h3>
        <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
          <span className="flex items-center gap-1">
            <Clock size={14} />
            {event.time}
          </span>
          <span className="flex items-center gap-1">
            <CalendarIcon size={14} />
            {event.duration}
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Calendar Section */}
        <div className="lg:w-2/3">
          <div className="bg-white rounded-xl shadow-sm p-6">
            {/* Calendar Header */}
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-semibold text-navy-900">
                  {months[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                </h2>
                <div className="flex items-center gap-1">
                  <button 
                    className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                    onClick={() => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() - 1)))}
                  >
                    <ChevronLeft size={18} className="text-gray-600" />
                  </button>
                  <button 
                    className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                    onClick={() => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() + 1)))}
                  >
                    <ChevronRight size={18} className="text-gray-600" />
                  </button>
                </div>
              </div>
              <button className="flex items-center gap-2 bg-navy-600 text-white px-4 py-2 rounded-lg hover:bg-navy-700 transition-colors text-sm">
                <Plus size={16} />
                <span>Schedule Session</span>
              </button>
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-px bg-gray-200 rounded-lg overflow-hidden">
              {/* Week days header */}
              {weekDays.map(day => (
                <div key={day} className="bg-gray-50 p-3 text-center">
                  <span className="text-sm font-medium text-gray-600">{day}</span>
                </div>
              ))}
              
              {/* Calendar days */}
              {getMonthDays().map((day, index) => {
                const isToday = day === new Date().getDate() && 
                              currentMonth.getMonth() === new Date().getMonth() &&
                              currentMonth.getFullYear() === new Date().getFullYear();
                const hasEvent = day && Math.random() > 0.8; // Simulated events

                return (
                  <div
                    key={index}
                    className={`bg-white p-3 min-h-[100px] ${
                      day ? 'hover:bg-gray-50 cursor-pointer' : ''
                    }`}
                  >
                    {day && (
                      <div className="relative">
                        <span className={`text-sm ${
                          isToday 
                            ? 'flex items-center justify-center w-7 h-7 rounded-full bg-navy-600 text-white'
                            : 'text-gray-700'
                        }`}>
                          {day}
                        </span>
                        {hasEvent && (
                          <div className="absolute left-1/2 -translate-x-1/2 w-1 h-1 bg-navy-600 rounded-full mt-1" />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Today's Schedule */}
        <div className="lg:w-1/3">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-navy-900 mb-4">Today's Schedule</h2>
            <div className="space-y-4">
              {scheduleEvents.map(event => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Schedule;